/**
 * Performance Tab for Admin Console
 *
 * Tabbed dashboard covering:
 * - Web Vitals (Core Web Vitals, Page Load Times, Recent Metrics stream)
 * - Search Analytics (Enhanced search metrics)
 * - Import Analytics (screenshot/text/link import stats)
 * - Feedback (unified general + import feedback with filters and CSV export)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  LinearProgress,
  useTheme,
  Alert,
  Tab,
  Tabs,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Button,
  Rating,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Refresh,
  TrendingUp,
  TrendingDown,
  Timer,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Storage,
  Search,
  FileDownload,
  Feedback as FeedbackIcon,
  Person,
  Email,
  Schedule,
  Info,
  MoreHoriz,
  Block,
  Delete,
  Image as ImageIcon,
  TextFields,
  Link as LinkIcon,
  Star,
  AutoAwesome,
} from '@mui/icons-material';
import {
  getPerformanceSummary,
  PerformanceSummary,
} from '../../services/performanceAggregationService';
import { font } from '../../utils/fontHelper';
import { PerformanceHistoryCharts } from './PerformanceHistoryCharts';
import { enhancedSearchAnalytics } from '../../utils/enhancedSearchAnalytics';
import {
  getDb,
  collection,
  query,
  orderBy,
  getDocs,
  unifiedGetDoc,
  unifiedUpdateDoc,
  unifiedDeleteDoc,
} from '@watchlist/shared';
import { nativeFirebase } from '../../services/nativeFirebaseService';
import { isCapacitorNative } from '../../utils/platformUtils';
import { formatFirebaseDate } from '../../utils/dateUtils';
import { glassStyles } from '../../tokens/designTokens';
import { importAnalyticsService, ImportStatistics } from '../../services/importAnalyticsService';

// ─── Web Vitals config ────────────────────────────────────────────────────────

const PAGE_LOAD_LABELS: Record<string, string> = {
  movie_detail_load: 'Movie detail',
  watchlist_detail_load: 'Watchlist',
  person_profile_load: 'Person',
  person_credits_load: 'Person credits',
  person_detail_load: 'Person credits (legacy)',
  search_page_load: 'Search',
  search_unified_results: 'Search — all tabs (results)',
  search_movies_results: 'Search — movies tab',
  search_watchlists_results: 'Search — watchlists',
  search_users_results: 'Search — users',
  search_people_results: 'Search — people',
  search_to_movie_open: 'Search → movie ready',
  home_page_load: 'Home',
  profile_page_load: 'Profile',
};

const VITAL_THRESHOLDS: Record<string, { good: number; poor: number; unit: string; description: string }> = {
  LCP: { good: 2500, poor: 4000, unit: 'ms', description: 'Largest Contentful Paint' },
  FID: { good: 100, poor: 300, unit: 'ms', description: 'First Input Delay' },
  CLS: { good: 0.1, poor: 0.25, unit: '', description: 'Cumulative Layout Shift' },
  FCP: { good: 1800, poor: 3000, unit: 'ms', description: 'First Contentful Paint' },
  TTFB: { good: 800, poor: 1800, unit: 'ms', description: 'Time to First Byte' },
  INP: { good: 200, poor: 500, unit: 'ms', description: 'Interaction to Next Paint' },
};

// ─── Feedback types ───────────────────────────────────────────────────────────

type FeedbackSource = 'app' | 'import';

interface UnifiedFeedbackItem {
  id: string;
  source: FeedbackSource;
  feedback: string;
  timestamp: any;
  createdAt?: any;
  // app feedback fields
  uid?: string;
  userEmail?: string;
  userName?: string;
  appVersion?: string;
  status?: 'new' | 'reviewed' | 'resolved';
  // import feedback fields
  userId?: string;
  rating?: number;
  category?: string;
  importSource?: 'screenshot' | 'text' | 'link';
  sessionId?: string;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface PerformanceTabProps {
  adminUid: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const StatCard: React.FC<{ label: string; value: string | number; sub?: string; color?: string; isDark: boolean }> = ({
  label, value, sub, color, isDark,
}) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
      }}
    >
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ mt: 1, fontWeight: 700, fontFamily: 'monospace', color: color ?? 'inherit' }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          {sub}
        </Typography>
      )}
    </Paper>
  );
};

// ─── Web Vitals sub-panel ────────────────────────────────────────────────────

const WebVitalsPanel: React.FC<{ adminUid: string }> = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<PerformanceSummary | null>(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPerformanceSummary();
      setSummary(data);
      setLastRefresh(new Date());
    } catch {
      setError('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const getRatingColor = (rating: 'good' | 'needs-improvement' | 'poor') => {
    if (rating === 'good') return '#30D158';
    if (rating === 'needs-improvement') return '#FF9F0A';
    return '#FF453A';
  };

  const getRatingForValue = (name: string, value: number): 'good' | 'needs-improvement' | 'poor' => {
    const t = VITAL_THRESHOLDS[name];
    if (!t) return 'good';
    if (value <= t.good) return 'good';
    if (value >= t.poor) return 'poor';
    return 'needs-improvement';
  };

  const formatValue = (name: string, value: number) =>
    name === 'CLS' ? value.toFixed(3) : `${Math.round(value)}ms`;

  const calcPassRate = (d: { goodCount: number; needsImprovementCount: number; poorCount: number }) => {
    const total = d.goodCount + d.needsImprovementCount + d.poorCount;
    return total === 0 ? 0 : (d.goodCount / total) * 100;
  };

  const getTrendIcon = (today?: number, yesterday?: number) => {
    if (!today || !yesterday) return null;
    const diff = today - yesterday;
    if (Math.abs(diff / yesterday) < 0.05) return null;
    return diff < 0
      ? <TrendingDown sx={{ color: '#30D158', fontSize: 16 }} />
      : <TrendingUp sx={{ color: '#FF453A', fontSize: 16 }} />;
  };

  if (loading && !summary) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  const hasNoData = !summary?.today && !summary?.yesterday && summary?.last7Days.length === 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: font() }}>Web Vitals</Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            Last updated: {lastRefresh.toLocaleTimeString()}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadData} disabled={loading}>
            <Refresh sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {hasNoData ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>No performance data yet</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Data is collected as users browse. Check DevTools console for{' '}
            <code>🟢 Web Vital [LCP]</code> entries.
          </Typography>
        </Alert>
      ) : (
        <>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.secondary }}>
            Core Web Vitals (Today)
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {Object.entries(VITAL_THRESHOLDS).map(([name, info]) => {
              const todayData = summary?.today?.webVitals?.[name];
              const yesterdayData = summary?.yesterday?.webVitals?.[name];
              return (
                <Grid item xs={6} sm={4} md={2} key={name}>
                  <Paper elevation={0} sx={{
                    p: 2, borderRadius: 2, height: '100%',
                    bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                  }}>
                    <Tooltip title={info.description}>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, cursor: 'help' }}>
                        {name}
                      </Typography>
                    </Tooltip>
                    {todayData ? (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                          <Typography variant="h5" sx={{
                            fontWeight: 700,
                            color: getRatingColor(getRatingForValue(name, todayData.avg)),
                            fontFamily: 'monospace',
                          }}>
                            {formatValue(name, todayData.avg)}
                          </Typography>
                          {getTrendIcon(todayData.avg, yesterdayData?.avg)}
                        </Box>
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Pass rate</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={calcPassRate(todayData)}
                              sx={{
                                flex: 1, height: 4, borderRadius: 2,
                                bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: calcPassRate(todayData) >= 75 ? '#30D158' : '#FF9F0A',
                                },
                              }}
                            />
                            <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 35 }}>
                              {Math.round(calcPassRate(todayData))}%
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, display: 'block', mt: 1 }}>
                          {todayData.count} samples
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" sx={{ color: theme.palette.text.disabled, mt: 1 }}>No data</Typography>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {summary?.historySlots && summary.historySlots.length > 0 && (
            <PerformanceHistoryCharts
              historySlots={summary.historySlots}
              pageLabelForKey={(k) => PAGE_LOAD_LABELS[k] ?? k.replace(/_/g, ' ')}
            />
          )}

          {summary?.today?.pageLoads && Object.keys(summary.today.pageLoads).length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.secondary }}>
                Page Load Times (Today)
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{
                mb: 4,
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                borderRadius: 2,
              }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Page</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Load</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Cache Hit</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Samples</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(summary.today.pageLoads).map(([pageName, data]) => (
                      <TableRow key={pageName}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Timer sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                            {PAGE_LOAD_LABELS[pageName] ?? pageName.replace(/_/g, ' ')}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Chip size="small" label={`${Math.round(data.avgTimeMs)}ms`} sx={{
                            bgcolor: data.avgTimeMs < 1000 ? 'rgba(48,209,88,0.1)' : data.avgTimeMs < 2000 ? 'rgba(255,159,10,0.1)' : 'rgba(255,69,58,0.1)',
                            color: data.avgTimeMs < 1000 ? '#30D158' : data.avgTimeMs < 2000 ? '#FF9F0A' : '#FF453A',
                            fontWeight: 600,
                          }} />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                            <Storage sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                            {(data as { cacheKnownCount?: number }).cacheKnownCount
                              ? `${Math.round(data.cacheHitRate * 100)}%`
                              : '—'}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{data.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {summary?.realtimeMetrics && summary.realtimeMetrics.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.secondary }}>
                Recent Metrics (Last 50)
              </Typography>
              <Box sx={{
                maxHeight: 300, overflowY: 'auto',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                borderRadius: 2, p: 2,
                bgcolor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
              }}>
                {summary.realtimeMetrics.map((metric, index) => (
                  <Box key={index} sx={{
                    display: 'flex', alignItems: 'center', gap: 2, py: 0.5,
                    borderBottom: index < summary.realtimeMetrics.length - 1
                      ? `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` : 'none',
                  }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, minWidth: 80, fontFamily: 'monospace' }}>
                      {new Date(metric.timestamp).toLocaleTimeString()}
                    </Typography>
                    <Chip size="small" label={metric.name} sx={{
                      minWidth: 50,
                      bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                      fontWeight: 600, fontSize: 11,
                    }} />
                    <Typography variant="body2" sx={{
                      fontFamily: 'monospace',
                      color: getRatingColor(metric.rating),
                      fontWeight: 600, flex: 1,
                    }}>
                      {formatValue(metric.name, metric.value)}
                    </Typography>
                    {metric.rating === 'good' && <CheckCircle sx={{ fontSize: 16, color: '#30D158' }} />}
                    {metric.rating === 'needs-improvement' && <Warning sx={{ fontSize: 16, color: '#FF9F0A' }} />}
                    {metric.rating === 'poor' && <ErrorIcon sx={{ fontSize: 16, color: '#FF453A' }} />}
                  </Box>
                ))}
              </Box>
            </>
          )}

          {summary?.last7Days && summary.last7Days.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 4, mb: 2, fontWeight: 600, color: theme.palette.text.secondary }}>
                7-Day History
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                borderRadius: 2,
              }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>LCP</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>FID</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>CLS</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Sessions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary.last7Days.map((day) => (
                      <TableRow key={day.date}>
                        <TableCell>{day.date}</TableCell>
                        <TableCell align="right">
                          {day.webVitals?.LCP
                            ? <Typography sx={{ color: getRatingColor(getRatingForValue('LCP', day.webVitals.LCP.avg)), fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>
                                {Math.round(day.webVitals.LCP.avg)}ms
                              </Typography>
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {day.webVitals?.FID
                            ? <Typography sx={{ color: getRatingColor(getRatingForValue('FID', day.webVitals.FID.avg)), fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>
                                {Math.round(day.webVitals.FID.avg)}ms
                              </Typography>
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {day.webVitals?.CLS
                            ? <Typography sx={{ color: getRatingColor(getRatingForValue('CLS', day.webVitals.CLS.avg)), fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>
                                {day.webVitals.CLS.avg.toFixed(3)}
                              </Typography>
                            : '-'}
                        </TableCell>
                        <TableCell align="right">{day.sessionCount || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Box>
  );
};

// ─── Search Analytics sub-panel ──────────────────────────────────────────────

const SearchAnalyticsPanel: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const m = enhancedSearchAnalytics.getAggregatedMetrics(24 * 60 * 60 * 1000);

  if (m.totalSearches === 0) {
    return (
      <Alert severity="info">
        No search analytics for the last 24 hours. Data is collected in-session and resets on page reload.
      </Alert>
    );
  }

  const improvement = m.averageLoadTimeEnhanced > 0
    ? Math.round((m.averageLoadTimeEnhanced - m.averageLoadTimeBasic) / m.averageLoadTimeEnhanced * 100)
    : 0;

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: font(), mb: 3 }}>
        Search Analytics (Last 24 Hours)
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}><StatCard label="Total Searches" value={m.totalSearches} isDark={isDark} /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Enhanced Usage" value={`${m.enhancedUsageRate.toFixed(1)}%`} sub={`${m.enhancedSearches + m.autoEnhancedSearches} searches`} isDark={isDark} /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Basic Search" value={`${m.averageLoadTimeBasic}ms`} sub={`${m.basicSearches} searches`} color="#30D158" isDark={isDark} /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Enhanced Search" value={`${m.averageLoadTimeEnhanced}ms`} sub={`${m.enhancedSearches + m.autoEnhancedSearches} searches`} color="#FF9F0A" isDark={isDark} /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Background Enhanced" value={m.backgroundEnhancedSearches} sub={`${m.backgroundEnhancementSuccessRate.toFixed(1)}% of searches`} isDark={isDark} /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Auto-Detected" value={m.autoEnhancedSearches} sub="Complex queries" isDark={isDark} /></Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Zero Results"
            value={`${m.zeroResultRate.toFixed(1)}%`}
            sub={`${Math.round(m.totalSearches * m.zeroResultRate / 100)} searches`}
            color={m.zeroResultRate > 10 ? '#FF453A' : m.zeroResultRate > 5 ? '#FF9F0A' : '#30D158'}
            isDark={isDark}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{
            p: 2, borderRadius: 2,
            bgcolor: isDark ? 'rgba(48,209,88,0.1)' : 'rgba(48,209,88,0.05)',
            border: `1px solid ${isDark ? 'rgba(48,209,88,0.3)' : 'rgba(48,209,88,0.2)'}`,
          }}>
            <Typography variant="caption" sx={{ color: '#30D158', fontWeight: 600 }}>Speed Improvement</Typography>
            <Typography variant="h5" sx={{ mt: 1, fontWeight: 700, fontFamily: 'monospace', color: '#30D158' }}>
              {improvement}%
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Basic vs Enhanced</Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

// ─── Import Analytics sub-panel ──────────────────────────────────────────────

const ImportAnalyticsPanel: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ImportStatistics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    importAnalyticsService.getImportStatistics()
      .then(setStats)
      .catch(() => setError('Failed to load import analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!stats || stats.totalImports === 0) {
    return <Alert severity="info">No import analytics yet. Data is recorded when users import movies.</Alert>;
  }

  const sourceIcons: Record<string, React.ReactNode> = {
    screenshot: <ImageIcon sx={{ fontSize: 14 }} />,
    text: <TextFields sx={{ fontSize: 14 }} />,
    link: <LinkIcon sx={{ fontSize: 14 }} />,
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: font(), mb: 3 }}>Import Analytics</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}><StatCard label="Total Imports" value={stats.totalImports} isDark={isDark} /></Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Success Rate"
            value={`${stats.successRate.toFixed(1)}%`}
            color={stats.successRate >= 80 ? '#30D158' : stats.successRate >= 60 ? '#FF9F0A' : '#FF453A'}
            isDark={isDark}
          />
        </Grid>
        <Grid item xs={6} sm={3}><StatCard label="Avg Processing" value={`${Math.round(stats.averageProcessingTime / 1000)}s`} isDark={isDark} /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Avg Movies/Import" value={stats.averageMoviesPerImport.toFixed(1)} isDark={isDark} /></Grid>
        {stats.userSatisfactionAverage > 0 && (
          <Grid item xs={6} sm={3}>
            <StatCard label="Avg Rating" value={`${stats.userSatisfactionAverage.toFixed(1)} / 5`} color="#FF9F0A" isDark={isDark} />
          </Grid>
        )}
      </Grid>

      {Object.keys(stats.sourceTypeDistribution).length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.secondary }}>
            By Source
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
            {Object.entries(stats.sourceTypeDistribution).map(([src, count]) => (
              <Paper key={src} elevation={0} sx={{
                px: 2.5, py: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1,
                bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
              }}>
                {sourceIcons[src]}
                <Typography sx={{ fontFamily: font(), fontWeight: 600, fontSize: 14, textTransform: 'capitalize' }}>{src}</Typography>
                <Chip size="small" label={count} sx={{ fontWeight: 700, fontFamily: 'monospace' }} />
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  ({((count / stats.totalImports) * 100).toFixed(0)}%)
                </Typography>
              </Paper>
            ))}
          </Box>
        </>
      )}

      {Object.keys(stats.errorFrequency).length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.secondary }}>
            Error Frequency
          </Typography>
          <TableContainer component={Paper} elevation={0} sx={{
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
            borderRadius: 2, mb: 4,
          }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Error Code</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(stats.errorFrequency)
                  .sort(([, a], [, b]) => b - a)
                  .map(([code, count]) => (
                    <TableRow key={code}>
                      <TableCell><Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>{code}</Typography></TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={count} sx={{ fontWeight: 600, bgcolor: 'rgba(255,69,58,0.1)', color: '#FF453A' }} />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

// ─── Unified Feedback sub-panel ───────────────────────────────────────────────

type FeedbackTypeFilter = 'all' | 'app' | 'import';
type StatusFilter = 'all' | 'new' | 'reviewed' | 'resolved';

const FeedbackPanel: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [items, setItems] = useState<UnifiedFeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<FeedbackTypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; item: UnifiedFeedbackItem } | null>(null);

  const formatDate = (ts: any) => formatFirebaseDate(ts, 'Unknown', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result: UnifiedFeedbackItem[] = [];

      if (isCapacitorNative()) {
        const appDocs = await nativeFirebase.queryDocuments<any>('feedback', [], { orderByField: 'createdAt', orderDirection: 'desc' });
        appDocs.forEach((d) => result.push({ ...d, source: 'app' }));
        const importDocs = await nativeFirebase.queryDocuments<any>('importFeedback', [], { orderByField: 'timestamp', orderDirection: 'desc' });
        importDocs.forEach((d) => result.push({ ...d, source: 'import' }));
      } else {
        // General feedback
        const appSnap = await getDocs(query(collection(getDb(), 'feedback'), orderBy('createdAt', 'desc')));
        appSnap.forEach((doc) => result.push({ id: doc.id, source: 'app', ...doc.data() } as UnifiedFeedbackItem));
        // Import feedback
        const importSnap = await getDocs(query(collection(getDb(), 'importFeedback'), orderBy('timestamp', 'desc')));
        importSnap.forEach((doc) => result.push({ id: doc.id, source: 'import', ...doc.data() } as UnifiedFeedbackItem));
      }

      // Enrich import feedback items that are missing userName/userEmail (older records)
      const missingUids = [
        ...new Set(
          result
            .filter((i) => i.source === 'import' && !i.userName && i.userId)
            .map((i) => i.userId as string)
        ),
      ];
      if (missingUids.length > 0) {
        const profileMap: Record<string, { displayName?: string; email?: string }> = {};
        await Promise.all(
          missingUids.map(async (uid) => {
            try {
              const snap = await unifiedGetDoc('userProfiles', uid);
              const exists = typeof snap.exists === 'function' ? snap.exists() : snap.exists;
              const data = typeof snap.data === 'function' ? snap.data() : snap.data;
              if (exists && data) {
                profileMap[uid] = { displayName: data.displayName, email: data.email };
              }
            } catch {
              // best-effort
            }
          })
        );
        result.forEach((item) => {
          if (item.source === 'import' && item.userId && profileMap[item.userId]) {
            item.userName ??= profileMap[item.userId].displayName;
            item.userEmail ??= profileMap[item.userId].email;
          }
        });
      }

      // Sort combined by date desc
      result.sort((a, b) => {
        const dateA = a.createdAt ?? a.timestamp;
        const dateB = b.createdAt ?? b.timestamp;
        const toMs = (d: any) => {
          if (!d) return 0;
          if (d?.toDate) return d.toDate().getTime();
          if (d?.seconds) return d.seconds * 1000;
          return new Date(d).getTime();
        };
        return toMs(dateB) - toMs(dateA);
      });

      setItems(result);
    } catch (err: any) {
      setError(err.message ?? 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((item) => {
    if (typeFilter !== 'all' && item.source !== typeFilter) return false;
    if (statusFilter !== 'all' && item.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        (item.feedback ?? '').toLowerCase().includes(q) ||
        (item.userName ?? '').toLowerCase().includes(q) ||
        (item.userEmail ?? '').toLowerCase().includes(q) ||
        (item.category ?? '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const handleUpdateStatus = async (item: UnifiedFeedbackItem, status: 'new' | 'reviewed' | 'resolved') => {
    if (item.source !== 'app') return;
    try {
      await unifiedUpdateDoc('feedback', item.id, { status });
      setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, status } : i));
    } catch (err: any) {
      setError(err.message ?? 'Failed to update status');
    }
    setMenuAnchor(null);
  };

  const handleDelete = async (item: UnifiedFeedbackItem) => {
    if (!window.confirm('Delete this feedback? This cannot be undone.')) return;
    try {
      const col = item.source === 'app' ? 'feedback' : 'importFeedback';
      await unifiedDeleteDoc(col, item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (err: any) {
      setError(err.message ?? 'Failed to delete feedback');
    }
    setMenuAnchor(null);
  };

  const downloadCSV = () => {
    const esc = (v: string) => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ['Date', 'Type', 'Source', 'Category', 'Rating', 'Status', 'User', 'Email', 'Feedback'];
    const rows = filtered.map((item) => [
      esc(formatDate(item.createdAt ?? item.timestamp)),
      esc(item.source),
      esc(item.importSource ?? ''),
      esc(item.category ?? ''),
      esc(item.rating != null ? String(item.rating) : ''),
      esc(item.status ?? ''),
      esc(item.userName ?? item.userId ?? ''),
      esc(item.userEmail ?? ''),
      esc(item.feedback ?? ''),
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `all_feedback_${new Date().toISOString().split('T')[0]}.csv`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusColor = (status?: string) => {
    if (status === 'new') return '#FF9F0A';
    if (status === 'reviewed') return theme.palette.primary.main;
    if (status === 'resolved') return '#30D158';
    return theme.palette.text.secondary;
  };

  const getCategoryColor = (cat?: string) => {
    if (cat === 'praise') return '#30D158';
    if (cat === 'bug') return '#FF9F0A';
    if (cat === 'suggestion') return theme.palette.info.main;
    return theme.palette.text.secondary;
  };

  const sourceIcon = (src: FeedbackSource, importSrc?: string) => {
    if (src === 'import') {
      if (importSrc === 'screenshot') return <ImageIcon sx={{ fontSize: 14 }} />;
      if (importSrc === 'text') return <TextFields sx={{ fontSize: 14 }} />;
      if (importSrc === 'link') return <LinkIcon sx={{ fontSize: 14 }} />;
      if (importSrc === 'recommendation') return <AutoAwesome sx={{ fontSize: 14 }} />;
    }
    return <FeedbackIcon sx={{ fontSize: 14 }} />;
  };

  const importSourceLabel = (importSrc?: string) => {
    if (importSrc === 'screenshot') return 'Screenshot';
    if (importSrc === 'text') return 'Text';
    if (importSrc === 'link') return 'Link';
    if (importSrc === 'recommendation') return 'Watch Now';
    return importSrc ?? '';
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: font() }}>
          All Feedback
          <Typography component="span" variant="body2" sx={{ ml: 1, color: theme.palette.text.secondary }}>
            ({filtered.length} of {items.length})
          </Typography>
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={downloadCSV}
            disabled={filtered.length === 0}
            size="small"
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
          >
            Export CSV
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={load} size="small"><Refresh /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search feedback..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ flex: '1 1 250px', minWidth: 180 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: theme.palette.text.secondary }} /></InputAdornment>,
          }}
        />

        {/* Type filter */}
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {(['all', 'app', 'import'] as FeedbackTypeFilter[]).map((f) => (
            <Chip
              key={f}
              label={{ all: 'All types', app: 'App feedback', import: 'Import feedback' }[f]}
              onClick={() => setTypeFilter(f)}
              color={typeFilter === f ? 'primary' : 'default'}
              variant={typeFilter === f ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Box>

        {/* Status filter (only relevant for app feedback) */}
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {(['all', 'new', 'reviewed', 'resolved'] as StatusFilter[]).map((f) => (
            <Chip
              key={f}
              label={f === 'all' ? 'Any status' : f.charAt(0).toUpperCase() + f.slice(1)}
              onClick={() => setStatusFilter(f)}
              color={statusFilter === f ? 'primary' : 'default'}
              variant={statusFilter === f ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Box>
      </Box>

      {/* Summary stats row */}
      {items.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Paper elevation={0} sx={{
            px: 2, py: 1, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
          }}>
            <FeedbackIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            <Typography variant="body2" sx={{ fontFamily: font() }}>
              <strong>{items.filter((i) => i.source === 'app').length}</strong> app
            </Typography>
          </Paper>
          <Paper elevation={0} sx={{
            px: 2, py: 1, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
          }}>
            <ImageIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            <Typography variant="body2" sx={{ fontFamily: font() }}>
              <strong>{items.filter((i) => i.source === 'import').length}</strong> import
            </Typography>
          </Paper>
          {(() => {
            const rated = items.filter((i) => i.rating != null);
            if (rated.length === 0) return null;
            const avg = rated.reduce((s, i) => s + (i.rating ?? 0), 0) / rated.length;
            return (
              <Paper elevation={0} sx={{
                px: 2, py: 1, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1,
                bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
              }}>
                <Star sx={{ fontSize: 16, color: '#FF9F0A' }} />
                <Typography variant="body2" sx={{ fontFamily: font() }}>
                  <strong>{avg.toFixed(1)}</strong> avg rating ({rated.length} rated)
                </Typography>
              </Paper>
            );
          })()}
        </Box>
      )}

      {/* List */}
      {filtered.length === 0 ? (
        <Box sx={{
          textAlign: 'center', py: 8,
          bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
          borderRadius: 2,
          border: `1px dashed ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
        }}>
          <FeedbackIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
          <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>No feedback found</Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 1 }}>
            {searchQuery || typeFilter !== 'all' || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Feedback submissions will appear here'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filtered.map((item) => (
            <Card key={`${item.source}-${item.id}`} sx={{
              bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
              borderLeft: `4px solid ${item.source === 'import' ? getCategoryColor(item.category) : getStatusColor(item.status)}`,
            }}>
              <CardContent>
                {/* Header row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {/* Source badge */}
                    <Chip
                      icon={sourceIcon(item.source, item.importSource)}
                      label={item.source === 'import'
                        ? `Import · ${importSourceLabel(item.importSource)}`
                        : 'App feedback'}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        bgcolor: item.source === 'import'
                          ? isDark ? 'rgba(125,122,255,0.15)' : 'rgba(125,122,255,0.1)'
                          : isDark ? 'rgba(48,209,88,0.15)' : 'rgba(48,209,88,0.1)',
                        color: item.source === 'import' ? theme.palette.primary.main : '#30D158',
                      }}
                    />
                    {/* Category */}
                    {item.category && (
                      <Chip
                        label={item.category.replace(/_/g, ' ')}
                        size="small"
                        sx={{ fontWeight: 500, textTransform: 'capitalize', color: getCategoryColor(item.category) }}
                        variant="outlined"
                      />
                    )}
                    {/* App feedback status */}
                    {item.source === 'app' && item.status && (
                      <Chip
                        label={item.status}
                        size="small"
                        sx={{ bgcolor: getStatusColor(item.status), color: '#fff', fontWeight: 600, textTransform: 'capitalize' }}
                      />
                    )}
                    {/* Rating */}
                    {item.rating != null && (
                      <Rating value={item.rating} readOnly size="small" sx={{ fontSize: 14 }} />
                    )}
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      <Schedule sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.5 }} />
                      {formatDate(item.createdAt ?? item.timestamp)}
                    </Typography>
                  </Box>

                  <IconButton size="small" onClick={(e) => setMenuAnchor({ element: e.currentTarget, item })}>
                    <MoreHoriz />
                  </IconButton>
                </Box>

                {/* Body */}
                <Typography sx={{ mb: 2, fontFamily: font(), fontSize: 15, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {item.feedback || <em style={{ color: theme.palette.text.disabled }}>No comment</em>}
                </Typography>

                {/* Footer */}
                <Box sx={{
                  display: 'flex', gap: 2, flexWrap: 'wrap', pt: 1.5,
                  borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                }}>
                  {(item.userName || item.userId) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Person sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        {item.userName ?? item.userId}
                      </Typography>
                    </Box>
                  )}
                  {item.userEmail && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Email sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>{item.userEmail}</Typography>
                    </Box>
                  )}
                  {item.appVersion && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Info sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>v{item.appVersion}</Typography>
                    </Box>
                  )}
                  {item.sessionId && (
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontFamily: 'monospace' }}>
                      session: {item.sessionId.slice(0, 8)}…
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Context menu */}
      <Menu
        anchorEl={menuAnchor?.element}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{ sx: { ...glassStyles.menu(isDark) } }}
      >
        {menuAnchor?.item.source === 'app' && [
          <MenuItem key="new" onClick={() => menuAnchor && handleUpdateStatus(menuAnchor.item, 'new')}>
            <ListItemIcon><FeedbackIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Mark as New</ListItemText>
          </MenuItem>,
          <MenuItem key="reviewed" onClick={() => menuAnchor && handleUpdateStatus(menuAnchor.item, 'reviewed')}>
            <ListItemIcon><Block fontSize="small" /></ListItemIcon>
            <ListItemText>Mark as Reviewed</ListItemText>
          </MenuItem>,
          <MenuItem key="resolved" onClick={() => menuAnchor && handleUpdateStatus(menuAnchor.item, 'resolved')}>
            <ListItemIcon><CheckCircle fontSize="small" /></ListItemIcon>
            <ListItemText>Mark as Resolved</ListItemText>
          </MenuItem>,
        ]}
        <MenuItem
          onClick={() => menuAnchor && handleDelete(menuAnchor.item)}
          sx={{ color: theme.palette.error.main }}
        >
          <ListItemIcon><Delete fontSize="small" sx={{ color: theme.palette.error.main }} /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

// ─── Main PerformanceTab (tabbed shell) ───────────────────────────────────────

const PerformanceTab: React.FC<PerformanceTabProps> = ({ adminUid }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 3,
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          '& .MuiTab-root': { textTransform: 'none', fontFamily: font(), fontWeight: 500, fontSize: 14 },
          '& .Mui-selected': { fontWeight: 700 },
        }}
      >
        <Tab label="Web Vitals" />
        <Tab label="Search" />
        <Tab label="Imports" />
        <Tab label="Feedback" />
      </Tabs>

      {tab === 0 && <WebVitalsPanel adminUid={adminUid} />}
      {tab === 1 && <SearchAnalyticsPanel />}
      {tab === 2 && <ImportAnalyticsPanel />}
      {tab === 3 && <FeedbackPanel />}
    </Box>
  );
};

export default PerformanceTab;
